import axios from 'axios';

const TOKEN_REFRESH_INTERVAL = 4 * 60 * 1000; // 4 min in ms

export const withReturn = (url, includePath = true) => {
  if (typeof window === 'undefined') {
    // server side rendering, so we don't know where to return to
    return url;
  }
  const next = includePath ? window.location.href : window.location.origin;
  const home = window.location.origin;
  const params = new URLSearchParams();
  params.append('next', next);
  params.append('home', home);
  return `${url}?${params.toString()}`;
};

export const getLogoutUrl = () => `${window.WillingConfig.portunusUrl}/api/logout`;

const defaultFetch = () =>
  axios({
    method: 'post',
    url: `${window.WillingConfig.portunusUrl}/api/auth/token/refresh/`,
    withCredentials: true,
  });

export const getLoginUrl = () => withReturn(`${window.WillingConfig.portunusUrl}/login`);

export const getLoginSettingsUrl = () => withReturn(`${window.WillingConfig.portunusUrl}`);

const defaultOnError = () => {
  window.location.replace(getLoginUrl());
};

class TokenFetcher {
  constructor() {
    this.fetchFunction = defaultFetch;
    this.onError = defaultOnError;
    this.onSuccess = () => null;
    this.currentToken = '';
    this.timerId = null;
    this.tokenCallbacks = [];
    this.lastFetchedAt = Date.now();
    this.failed = false;
  }

  get isLastFetchExpired() {
    return Date.now() - this.lastFetchedAt > TOKEN_REFRESH_INTERVAL;
  }

  get accessToken() {
    return new Promise(resolve => {
      if (this.failed) {
        resolve(this.currentToken);
      }
      if (this.currentToken) {
        if (this.isLastFetchExpired) {
          this.startAutoFetch().then(() => resolve(this.currentToken));
        } else {
          resolve(this.currentToken);
        }
      } else {
        this.tokenCallbacks.push(resolve);
      }
    });
  }

  fetchToken = async () => {
    try {
      this.currentToken = '';
      const response = await this.fetchFunction();
      this.currentToken = response.data.access;
      this.lastFetchedAt = Date.now();
      this.onSuccess(this.currentToken);
      this.clearCallbacks();
      return true;
    } catch (error) {
      if (error.response) {
        if ([400, 401].includes(error.response.status)) {
          this.failed = true;
          this.clearToken();
        }
      }
      this.onError(error);
    }
    return false;
  };

  clearToken() {
    this.currentToken = '';
    clearInterval(this.timerId);
    this.timerId = null;
    this.clearCallbacks();
  }

  startAutoFetch() {
    if (this.timerId) {
      clearInterval(this.timerId);
    }
    this.timerId = setInterval(this.fetchToken, TOKEN_REFRESH_INTERVAL);
    return this.fetchToken();
  }

  clearCallbacks() {
    this.tokenCallbacks.forEach(cb => cb(this.currentToken));
    this.tokenCallbacks = [];
  }

  start(fetchFn, onSuccess, onError) {
    this.fetchFunction = fetchFn || this.fetchFunction;
    this.onSuccess = onSuccess || this.onSuccess;
    this.onError = onError || this.onError;
    if (!this.timerId) {
      this.startAutoFetch();
    }
  }
}

const tokenFetcher = new TokenFetcher();

export default tokenFetcher;
